import React, { useState, useEffect, useRef } from 'react';
import RequestService from '../services/requestService';
import DateUtils from '../utils/dateUtils';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PendingActions as PendingIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  Assessment as AssessmentIcon,
  Handshake as HandshakeIcon,
  Send as SendIcon,
  AttachMoney as MoneyIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';


const RequestsPage = () => {
  // Zustand für Anfragen
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Zustand für erweiterte Zeilen
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  // Laden der Anfragen vom Backend
  useEffect(() => {
    const loadRequests = async () => {
      try {
        const requestsData = await RequestService.getAllRequests();
        console.log("Geladene Anfragen in RequestsPage:", requestsData);
        
        if (Array.isArray(requestsData) && requestsData.length > 0) {
          setRequests(requestsData);
        } else {
          console.error("Keine Anfragen geladen oder ungültiges Format:", requestsData);
          // Versuche, die Daten direkt aus dem Fallback zu laden
          const fallbackData = RequestService.getFallbackData().requests;
          console.log("Verwende Fallback-Daten:", fallbackData);
          setRequests(fallbackData);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Fehler beim Laden der Anfragen:", error);
        // Versuche, die Daten direkt aus dem Fallback zu laden
        const fallbackData = RequestService.getFallbackData().requests;
        console.log("Verwende Fallback-Daten nach Fehler:", fallbackData);
        setRequests(fallbackData);
        setLoading(false);
      }
    };

    loadRequests();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleActionClick = (request) => {
    // Zeile ein- oder ausklappen
    setExpandedRow(expandedRow === request.id ? null : request.id);
  };

  // Filtern der Anfragen basierend auf Suchbegriff, Statusfilter und Tab
  const getFilteredRequests = () => {
    let filteredByStatus = requests;
    
    // Filter basierend auf Tab
    if (tabValue === 1) { // Neue Anfragen
      filteredByStatus = requests.filter(r => ['new'].includes(r.status));
    } else if (tabValue === 2) { // In Bearbeitung
      filteredByStatus = requests.filter(r => ['feasibility', 'negotiation', 'in_progress'].includes(r.status));
    } else if (tabValue === 3) { // Abgeschlossen
      filteredByStatus = requests.filter(r => ['completed', 'payment_pending', 'delivered', 'rejected'].includes(r.status));
    }
    
    // Zusätzlicher Filter basierend auf Dropdown
    if (statusFilter !== 'all') {
      filteredByStatus = filteredByStatus.filter(r => r.status === statusFilter);
    }
    
    // Filter basierend auf Suchbegriff
    return filteredByStatus.filter(request => {
      return searchTerm === '' || 
        request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.company.toLowerCase().includes(searchTerm.toLowerCase());
    });
  };

  const filteredRequests = getFilteredRequests();

  const getStatusInfo = (status) => {
    switch (status) {
      case 'new':
        return { color: 'primary', label: 'Neu', icon: <PendingIcon fontSize="small" /> };
      case 'feasibility':
        return { color: 'warning', label: 'Machbarkeitsprüfung', icon: <AssessmentIcon fontSize="small" /> };
      case 'negotiation':
        return { color: 'warning', label: 'In Verhandlung', icon: <HandshakeIcon fontSize="small" /> };
      case 'in_progress':
        return { color: 'warning', label: 'In Bearbeitung', icon: <PlayArrowIcon fontSize="small" /> };
      case 'completed':
        return { color: 'info', label: 'Abgeschlossen', icon: <CheckCircleIcon fontSize="small" /> };
      case 'payment_pending':
        return { color: 'info', label: 'Zahlung ausstehend', icon: <MoneyIcon fontSize="small" /> };
      case 'delivered':
        return { color: 'success', label: 'Übermittelt', icon: <SendIcon fontSize="small" /> };
      case 'rejected':
        return { color: 'error', label: 'Abgelehnt', icon: <CancelIcon fontSize="small" /> };
      default:
        return { color: 'default', label: status, icon: null };
    }
  };

  const getActionButtonText = (status) => {
    switch (status) {
      case 'new':
        return 'Machbarkeit prüfen';
      case 'feasibility':
        return 'Machbarkeit prüfen';
      case 'negotiation':
        return 'Verhandlung fortsetzen';
      case 'in_progress':
        return 'Bearbeitung anzeigen';
      case 'completed':
        return 'Ergebnisse übermitteln';
      case 'payment_pending':
        return 'Zahlungsstatus prüfen';
      case 'delivered':
        return 'Details anzeigen';
      case 'rejected':
        return 'Details anzeigen';
      default:
        return 'Details anzeigen';
    }
  };
  
  // Komponenten für die verschiedenen Anfragenstatus
  const renderExpandedContent = (request) => {
    // Bestimme verfügbare Schritte basierend auf dem aktuellen Status
    const availableSteps = getAvailableSteps(request.status);
    return <RequestDetailsTabs request={request} availableSteps={availableSteps} />;
  };
  
  // Bestimmt, welche Schritte für den aktuellen Status verfügbar sind
  const getAvailableSteps = (status) => {
    const allSteps = [
      { id: 'details', label: 'Details', available: true },
      { id: 'feasibility', label: 'Machbarkeitsprüfung', available: false },
      { id: 'negotiation', label: 'Verhandlung', available: false },
      { id: 'execution', label: 'Ausführung', available: false },
      { id: 'results', label: 'Ergebnisse', available: false }
    ];
    
    switch (status) {
      case 'new':
        return allSteps.map(step => ({ 
          ...step, 
          available: step.id === 'details' || step.id === 'feasibility'
        }));
      case 'feasibility':
        return allSteps.map(step => ({ 
          ...step, 
          available: step.id === 'details' || step.id === 'feasibility'
        }));
      case 'negotiation':
        return allSteps.map(step => ({ 
          ...step, 
          available: step.id === 'details' || step.id === 'feasibility' || step.id === 'negotiation'
        }));
      case 'in_progress':
        return allSteps.map(step => ({ 
          ...step, 
          available: step.id === 'details' || step.id === 'feasibility' || 
                    step.id === 'negotiation' || step.id === 'execution'
        }));
      case 'completed':
      case 'payment_pending':
      case 'delivered':
        return allSteps.map(step => ({ ...step, available: true }));
      case 'rejected':
        // Bei abgelehnten Anfragen nur Details und den letzten erreichten Schritt anzeigen
        return allSteps.map(step => ({ 
          ...step, 
          available: step.id === 'details' || step.id === 'feasibility'
        }));
      default:
        return allSteps.map(step => ({ ...step, available: step.id === 'details' }));
    }
  };
  
  // Komponente für die Tabs in der erweiterten Ansicht
  const RequestDetailsTabs = ({ request, availableSteps }) => {
    const [activeTab, setActiveTab] = useState('details');
    const [cardHeights, setCardHeights] = useState({});
    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    
    // Setze den aktiven Tab basierend auf dem Status
    useEffect(() => {
      switch (request.status) {
        case 'feasibility':
          setActiveTab('feasibility');
          break;
        case 'negotiation':
          setActiveTab('negotiation');
          break;
        case 'in_progress':
          setActiveTab('execution');
          break;
        case 'completed':
        case 'payment_pending':
          setActiveTab('results');
          break;
        case 'delivered':
          setActiveTab('results');
          break;
        default:
          setActiveTab('details');
      }
    }, [request.status]);

    // Effekt zum Messen und Anpassen der Kartenhöhen
    useEffect(() => {
      const updateCardHeights = () => {
        if (leftCardRef.current && rightCardRef.current) {
          const leftHeight = leftCardRef.current.clientHeight;
          const rightHeight = rightCardRef.current.clientHeight;
          const maxHeight = Math.max(leftHeight, rightHeight);
          
          setCardHeights({
            ...cardHeights,
            [activeTab]: maxHeight
          });
        }
      };

      // Warte einen Moment, bis die Karten gerendert sind
      const timer = setTimeout(updateCardHeights, 100);

      // Event-Listener für Größenänderungen
      window.addEventListener('resize', updateCardHeights);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCardHeights);
      };
    }, [activeTab, request]);
    
    const handleTabChange = (event, newValue) => {
      setActiveTab(newValue);
    };
    
    const renderTabContent = () => {
      switch (activeTab) {
        case 'details':
          return <RequestDetails request={request} />;
        case 'feasibility':
          return <FeasibilityDetails request={request} />;
        case 'negotiation':
          return <NegotiationDetails request={request} />;
        case 'execution':
          return <ExecutionDetails request={request} />;
        case 'results':
          return request.status === 'delivered' ? 
            <DeliveredDetails request={request} /> : 
            <ResultsDetails request={request} />;
        default:
          return <RequestDetails request={request} />;
      }
    };
    
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="request detail tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {availableSteps.map(step => (
              <Tab 
                key={step.id}
                label={step.label} 
                value={step.id} 
                disabled={!step.available}
                sx={{
                  opacity: step.available ? 1 : 0.5,
                  '&.Mui-disabled': {
                    color: 'text.disabled',
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ p: 1 }}>
          {renderTabContent()}
        </Box>
      </Box>
    );
  };
  
  // Komponente für neue Anfragen
  const NewRequestDetails = ({ request }) => {
    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(0);
    
    // Effekt zum Messen und Anpassen der Kartenhöhen
    useEffect(() => {
      const updateCardHeights = () => {
        if (leftCardRef.current && rightCardRef.current) {
          const leftHeight = leftCardRef.current.clientHeight;
          const rightHeight = rightCardRef.current.clientHeight;
          const maxHeight = Math.max(leftHeight, rightHeight);
          setCardHeight(maxHeight);
        }
      };

      // Warte einen Moment, bis die Karten gerendert sind
      const timer = setTimeout(updateCardHeights, 100);

      // Event-Listener für Größenänderungen
      window.addEventListener('resize', updateCardHeights);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCardHeights);
      };
    }, [request]);
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card ref={leftCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Anfrageinformationen" />
            <Divider />
            <CardContent>
              <Typography variant="body1" paragraph>
                <strong>Beschreibung:</strong> {request.description}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Unternehmen:</strong> {request.company}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Eingangsdatum:</strong> {DateUtils.formatDate(request.date)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card ref={rightCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Nächste Schritte" />
            <Divider />
            <CardContent>
              <Typography variant="body1" paragraph>
                Starten Sie die Machbarkeitsprüfung, um zu ermitteln, ob die angeforderten Daten verfügbar sind.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => {
                    console.log("Navigiere zu Machbarkeitsprüfung mit ID:", request.id);
                    window.location.href = `/feasibility-check?id=${request.id}`;
                  }}
                >
                  Zur Machbarkeitsprüfung
                </Button>
                <Button 
                  variant="outlined" 
                  color="error"
                >
                  Anfrage ablehnen
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  // Komponente für Anfragen in Machbarkeitsprüfung
  const FeasibilityDetails = ({ request }) => {
    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(0);
    
    // Effekt zum Messen und Anpassen der Kartenhöhen
    useEffect(() => {
      const updateCardHeights = () => {
        if (leftCardRef.current && rightCardRef.current) {
          const leftHeight = leftCardRef.current.clientHeight;
          const rightHeight = rightCardRef.current.clientHeight;
          const maxHeight = Math.max(leftHeight, rightHeight);
          setCardHeight(maxHeight);
        }
      };

      // Warte einen Moment, bis die Karten gerendert sind
      const timer = setTimeout(updateCardHeights, 100);

      // Event-Listener für Größenänderungen
      window.addEventListener('resize', updateCardHeights);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCardHeights);
      };
    }, [request]);
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card ref={leftCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Machbarkeitsprüfung" />
            <Divider />
            <CardContent>
              <Typography variant="body1" paragraph>
                Die Machbarkeitsprüfung für diese Anfrage wurde begonnen.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Geschätzte Patientenzahl:</strong> {request.patientCount || 'Noch nicht ermittelt'}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={request.feasibilityProgress || 60} 
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary" align="right">
                {request.feasibilityProgress || 60}% abgeschlossen
              </Typography>
              
              {request.variableAvailability && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Variablenverfügbarkeit:</strong>
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(request.variableAvailability).map(([variable, available], index) => (
                      <Grid item xs={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {available ? 
                            <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} /> : 
                            <ErrorIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                          }
                          <Typography variant="body2">
                            {variable}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card ref={rightCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Aktionen" />
            <Divider />
            <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body1" align="center" paragraph>
                Setzen Sie mit der Machbarkeitsprüfung fort, um die Verfügbarkeit der angeforderten Daten zu ermitteln und eine Grundlage für die Verhandlung zu schaffen.
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                size="large"
                onClick={() => {
                  console.log("Navigiere zu Machbarkeitsprüfung mit ID:", request.id);
                  window.location.href = `/feasibility-check?id=${request.id}`;
                }}
                sx={{ mb: 2, px: 4, py: 1.5 }}
              >
                Zur Machbarkeitsprüfung
              </Button>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                Nach Abschluss der Machbarkeitsprüfung können Sie in die Verhandlungsphase übergehen.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  // Komponente für Anfragen in Verhandlung
  const NegotiationDetails = ({ request }) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const options = { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      };
      return new Date(dateString).toLocaleDateString('de-DE', options);
    };

    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(0);
    
    // Effekt zum Messen und Anpassen der Kartenhöhen
    useEffect(() => {
      const updateCardHeights = () => {
        if (leftCardRef.current && rightCardRef.current) {
          const leftHeight = leftCardRef.current.clientHeight;
          const rightHeight = rightCardRef.current.clientHeight;
          const maxHeight = Math.max(leftHeight, rightHeight);
          setCardHeight(maxHeight);
        }
      };

      // Warte einen Moment, bis die Karten gerendert sind
      const timer = setTimeout(updateCardHeights, 100);

      // Event-Listener für Größenänderungen
      window.addEventListener('resize', updateCardHeights);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCardHeights);
      };
    }, [request]);

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card ref={leftCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Verhandlungsdetails" />
            <Divider />
            <CardContent>
              <Typography variant="body1" paragraph>
                <strong>Patientenzahl:</strong> {request.patientCount}
              </Typography>
              {request.negotiation && (
                <>
                  <Typography variant="body1" paragraph>
                    <strong>Initiales Angebot:</strong> {request.negotiation.initialOffer} €
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Aktuelles Angebot:</strong> {request.negotiation.currentOffer} €
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Ihr Gegenangebot:</strong> {request.negotiation.counterOffer} €
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Letzte Aktivität:</strong> {request.negotiation.messages && 
                      formatDate(request.negotiation.messages[request.negotiation.messages.length - 1]?.timestamp)}
                  </Typography>
                </>
              )}
              {!request.negotiation && (
                <>
                  <Typography variant="body1" paragraph>
                    <strong>Aktuelles Angebot:</strong> {request.price} €
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Letzte Aktivität:</strong> 2 Stunden zuvor
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card ref={rightCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Kommunikation" />
            <Divider />
            <CardContent>
              <List dense>
                {request.negotiation && request.negotiation.messages ? (
                  request.negotiation.messages.slice(-2).map((msg, index) => (
                    <ListItem key={index}>
                      <ListItemText 
                        primary={msg.sender} 
                        secondary={msg.content} 
                      />
                    </ListItem>
                  ))
                ) : (
                  <>
                    <ListItem>
                      <ListItemText 
                        primary="Pharma AG" 
                        secondary="Wir bieten 4.000 € für die Analyse." 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Universitätsklinikum" 
                        secondary="Unser Gegenangebot beträgt 5.000 €." 
                      />
                    </ListItem>
                  </>
                )}
              </List>
              <Button 
                variant="contained" 
                color="primary"
                fullWidth
                onClick={() => window.location.href = `/negotiation?id=${request.id}`}
                sx={{ mt: 2 }}
              >
                Zur Verhandlung
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  // Komponente für Anfragen in Bearbeitung
  const ExecutionDetails = ({ request }) => {
    const formatDate = (dateString) => {
      if (!dateString) return 'Noch nicht gestartet';
      const options = { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      };
      return new Date(dateString).toLocaleDateString('de-DE', options);
    };

    const getStepIcon = (status) => {
      switch (status) {
        case 'completed':
          return <CheckCircleIcon color="success" />;
        case 'in_progress':
          return <PendingIcon color="warning" />;
        case 'error':
          return <ErrorIcon color="error" />;
        case 'pending':
        default:
          return <PendingIcon color="disabled" />;
      }
    };

    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(0);
    
    // Effekt zum Messen und Anpassen der Kartenhöhen
    useEffect(() => {
      const updateCardHeights = () => {
        if (leftCardRef.current && rightCardRef.current) {
          const leftHeight = leftCardRef.current.clientHeight;
          const rightHeight = rightCardRef.current.clientHeight;
          const maxHeight = Math.max(leftHeight, rightHeight);
          setCardHeight(maxHeight);
        }
      };

      // Warte einen Moment, bis die Karten gerendert sind
      const timer = setTimeout(updateCardHeights, 100);

      // Event-Listener für Größenänderungen
      window.addEventListener('resize', updateCardHeights);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCardHeights);
      };
    }, [request]);

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card ref={leftCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Ausführungsdetails" />
            <Divider />
            <CardContent>
              <Typography variant="body1" paragraph>
                <strong>Patientenzahl:</strong> {request.patientCount}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Preis:</strong> {request.price} €
              </Typography>
              {request.execution && (
                <>
                  <Typography variant="body1" paragraph>
                    <strong>Startdatum:</strong> {DateUtils.formatDate(request.execution.startDate)}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Gesamtfortschritt:</strong>
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={request.execution.progress} 
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" align="right">
                    {request.execution.progress}% abgeschlossen
                  </Typography>
                </>
              )}
              {!request.execution && (
                <>
                  <Typography variant="body1" paragraph>
                    <strong>Gesamtfortschritt:</strong>
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={45} 
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" align="right">
                    45% abgeschlossen
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card ref={rightCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Prozessschritte" />
            <Divider />
            <CardContent>
              <List dense>
                {request.execution && request.execution.steps ? (
                  request.execution.steps.slice(0, 3).map((step, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {getStepIcon(step.status)}
                      </ListItemIcon>
                      <ListItemText 
                        primary={step.name} 
                        secondary={
                          step.status === 'completed' ? 
                            `Abgeschlossen (${formatDate(step.endTime)})` : 
                          step.status === 'in_progress' ? 
                            `In Bearbeitung (${step.progress}%)` : 
                            'Ausstehend'
                        } 
                      />
                    </ListItem>
                  ))
                ) : (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Datenextraktion" 
                        secondary="Abgeschlossen" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Pseudonymisierung" 
                        secondary="Abgeschlossen" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PendingIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Datenharmonisierung" 
                        secondary="In Bearbeitung (65%)" 
                      />
                    </ListItem>
                  </>
                )}
              </List>
              <Button 
                variant="contained" 
                color="primary"
                fullWidth
                onClick={() => window.location.href = `/order-execution?id=${request.id}`}
                sx={{ mt: 2 }}
              >
                Zur Ausführung
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  // Komponente für abgeschlossene Anfragen
  const ResultsDetails = ({ request }) => {
    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(0);
    
    // Effekt zum Messen und Anpassen der Kartenhöhen
    useEffect(() => {
      const updateCardHeights = () => {
        if (leftCardRef.current && rightCardRef.current) {
          const leftHeight = leftCardRef.current.clientHeight;
          const rightHeight = rightCardRef.current.clientHeight;
          const maxHeight = Math.max(leftHeight, rightHeight);
          setCardHeight(maxHeight);
        }
      };

      // Warte einen Moment, bis die Karten gerendert sind
      const timer = setTimeout(updateCardHeights, 100);

      // Event-Listener für Größenänderungen
      window.addEventListener('resize', updateCardHeights);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCardHeights);
      };
    }, [request]);
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card ref={leftCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Ergebnisdetails" />
            <Divider />
            <CardContent>
              <Typography variant="body1" paragraph>
                <strong>Patientenzahl:</strong> {request.patientCount}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Preis:</strong> {request.price} €
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Status:</strong> {request.status === 'completed' ? 'Abgeschlossen' : 'Zahlung ausstehend'}
              </Typography>
              {request.results && (
                <>
                  <Typography variant="body1" paragraph>
                    <strong>Abschlussdatum:</strong> {DateUtils.formatDate(request.results.completionDate)}
                  </Typography>
                  {request.status === 'payment_pending' && (
                    <Typography variant="body1" paragraph>
                      <strong>Zahlungsfällig bis:</strong> {DateUtils.formatDate(request.results.paymentDue)}
                    </Typography>
                  )}
                  <Typography variant="body1" paragraph>
                    <strong>Verfügbare Dateien:</strong> {request.results.files ? request.results.files.length : 4}
                  </Typography>
                </>
              )}
              {!request.results && (
                <Typography variant="body1" paragraph>
                  <strong>Verfügbare Dateien:</strong> 4
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card ref={rightCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Aktionen" />
            <Divider />
            <CardContent>
              <Button 
                variant="contained" 
                color="primary"
                fullWidth
                onClick={() => window.location.href = `/results-delivery?id=${request.id}`}
                sx={{ mb: 2 }}
              >
                {request.status === 'completed' ? 'Ergebnisse übermitteln' : 'Zahlungsstatus prüfen'}
              </Button>
              <Button 
                variant="outlined"
                fullWidth
                startIcon={<DownloadIcon />}
              >
                Ergebnisse herunterladen
              </Button>
              {request.results && request.results.keyFindings && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Wichtigste Ergebnisse:</strong>
                  </Typography>
                  <List dense>
                    {request.results.keyFindings.slice(0, 2).map((finding, index) => (
                      <ListItem key={index} sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={finding}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  // Komponente für übermittelte Anfragen
  const DeliveredDetails = ({ request }) => {
    const getFileIcon = (type) => {
      switch (type) {
        case 'pdf':
          return <PdfIcon color="error" />;
        default:
          return <FileIcon color="primary" />;
      }
    };

    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(0);
    
    // Effekt zum Messen und Anpassen der Kartenhöhen
    useEffect(() => {
      const updateCardHeights = () => {
        if (leftCardRef.current && rightCardRef.current) {
          const leftHeight = leftCardRef.current.clientHeight;
          const rightHeight = rightCardRef.current.clientHeight;
          const maxHeight = Math.max(leftHeight, rightHeight);
          setCardHeight(maxHeight);
        }
      };

      // Warte einen Moment, bis die Karten gerendert sind
      const timer = setTimeout(updateCardHeights, 100);

      // Event-Listener für Größenänderungen
      window.addEventListener('resize', updateCardHeights);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCardHeights);
      };
    }, [request]);

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card ref={leftCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Übermittlungsdetails" />
            <Divider />
            <CardContent>
              <Typography variant="body1" paragraph>
                <strong>Patientenzahl:</strong> {request.patientCount}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Preis:</strong> {request.price} €
              </Typography>
              {request.results && (
                <>
                  <Typography variant="body1" paragraph>
                    <strong>Abschlussdatum:</strong> {DateUtils.formatDate(request.results.completionDate)}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Übermittelt am:</strong> {DateUtils.formatDate(request.results.deliveryDate || '18.03.2025')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Zahlungsstatus:</strong> {request.results.paymentStatus === 'completed' ? 'Bezahlt' : 'Ausstehend'}
                  </Typography>
                  {request.results.paymentDate && (
                    <Typography variant="body1" paragraph>
                      <strong>Bezahlt am:</strong> {DateUtils.formatDate(request.results.paymentDate)}
                    </Typography>
                  )}
                </>
              )}
              {!request.results && (
                <>
                  <Typography variant="body1" paragraph>
                    <strong>Übermittelt am:</strong> {DateUtils.formatDate('18.03.2025')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Zahlungsstatus:</strong> Bezahlt
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card ref={rightCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Übermittelte Dateien" />
            <Divider />
            <CardContent>
              <List dense>
                {request.results && request.results.files ? (
                  request.results.files.slice(0, 2).map((file, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {getFileIcon(file.type)}
                      </ListItemIcon>
                      <ListItemText 
                        primary={file.name} 
                        secondary={file.size} 
                      />
                    </ListItem>
                  ))
                ) : (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <PdfIcon color="error" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Hauptbericht.pdf" 
                        secondary="2.4 MB" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FileIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Statistische_Auswertung.xlsx" 
                        secondary="1.8 MB" 
                      />
                    </ListItem>
                  </>
                )}
              </List>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                <Button 
                  variant="outlined"
                  fullWidth
                  startIcon={<DownloadIcon />}
                >
                  Alle Dateien herunterladen
                </Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  fullWidth
                  onClick={() => window.location.href = `/results-delivery?id=${request.id}`}
                >
                  Zu den Ergebnissen
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  // Komponente für abgelehnte Anfragen
  const RejectedDetails = ({ request }) => {
    // Bestimme den letzten abgeschlossenen Schritt (falls nicht vorhanden, setze 'details')
    request.lastCompletedStep = request.lastCompletedStep || 'details';
    
    const formatDate = (dateString) => {
      if (!dateString) return '';
      return dateString;
    };
    
    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(0);
    
    // Effekt zum Messen und Anpassen der Kartenhöhen
    useEffect(() => {
      const updateCardHeights = () => {
        if (leftCardRef.current && rightCardRef.current) {
          const leftHeight = leftCardRef.current.clientHeight;
          const rightHeight = rightCardRef.current.clientHeight;
          const maxHeight = Math.max(leftHeight, rightHeight);
          setCardHeight(maxHeight);
        }
      };

      // Warte einen Moment, bis die Karten gerendert sind
      const timer = setTimeout(updateCardHeights, 100);

      // Event-Listener für Größenänderungen
      window.addEventListener('resize', updateCardHeights);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCardHeights);
      };
    }, [request]);
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card ref={leftCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Ablehnungsdetails" />
            <Divider />
            <CardContent>
              <Typography variant="body1" paragraph>
                <strong>Abgelehnt am:</strong> {DateUtils.formatDate(request.rejectionDate || '16.02.2025')}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Grund:</strong> {request.rejectionReason || 'Die angeforderten Daten sind in unserem System nicht in ausreichender Qualität verfügbar.'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card ref={rightCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Kommunikation" />
            <Divider />
            <CardContent>
              <List dense>
                {request.communication ? (
                  request.communication.map((msg, index) => (
                    <ListItem key={index}>
                      <ListItemText 
                        primary={msg.sender} 
                        secondary={
                          <>
                            {msg.content}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatDate(msg.timestamp)}
                            </Typography>
                          </>
                        } 
                      />
                    </ListItem>
                  ))
                ) : (
                  <>
                    <ListItem>
                      <ListItemText 
                        primary="Universitätsklinikum" 
                        secondary="Leider müssen wir Ihre Anfrage ablehnen, da die angeforderten Daten in unserem System nicht in ausreichender Qualität verfügbar sind." 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Virology Research" 
                        secondary="Vielen Dank für die Rückmeldung. Können Sie uns mitteilen, welche Daten verfügbar wären?" 
                      />
                    </ListItem>
                  </>
                )}
              </List>
              <Button 
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
              >
                Nachricht senden
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  // Allgemeine Komponente für Anfragendetails
  const RequestDetails = ({ request }) => {
    // Verwende DateUtils für Datumsformatierung
    const formatDate = DateUtils.formatDate;
    const getDuration = DateUtils.getDuration;
    
    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(0);
    
    // Effekt zum Messen und Anpassen der Kartenhöhen
    useEffect(() => {
      const updateCardHeights = () => {
        if (leftCardRef.current && rightCardRef.current) {
          const leftHeight = leftCardRef.current.clientHeight;
          const rightHeight = rightCardRef.current.clientHeight;
          const maxHeight = Math.max(leftHeight, rightHeight);
          setCardHeight(maxHeight);
        }
      };

      // Warte einen Moment, bis die Karten gerendert sind
      const timer = setTimeout(updateCardHeights, 100);

      // Event-Listener für Größenänderungen
      window.addEventListener('resize', updateCardHeights);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCardHeights);
      };
    }, [request]);
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card ref={leftCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
            <CardHeader title="Anfragendetails" />
            <Divider />
            <CardContent>
              <Typography variant="body1" paragraph>
                <strong>Titel:</strong> {request.title}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Unternehmen:</strong> {request.company}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Eingangsdatum:</strong> {request.date}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Beschreibung:</strong> {request.description}
              </Typography>
              {request.patientCount && (
                <Typography variant="body1" paragraph>
                  <strong>Patientenzahl:</strong> {request.patientCount}
                </Typography>
              )}
              {request.price && (
                <Typography variant="body1" paragraph>
                  <strong>Preis:</strong> {request.price} €
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {request.statusHistory && (
          <Grid item xs={12} md={6}>
            <Card ref={rightCardRef} sx={{ height: cardHeight > 0 ? cardHeight : 'auto' }}>
              <CardHeader title="Status-Verlauf" />
              <Divider />
              <CardContent>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 1
                }}>
                  {Object.entries(request.statusHistory).map(([status, dates], index) => {
                    // Status-Namen für die Anzeige formatieren
                    const statusNames = {
                      'new': 'Neu',
                      'feasibility': 'Machbarkeitsprüfung',
                      'negotiation': 'Verhandlung',
                      'in_progress': 'In Bearbeitung',
                      'completed': 'Abgeschlossen',
                      'payment_pending': 'Zahlung ausstehend',
                      'delivered': 'Übermittelt',
                      'rejected': 'Abgelehnt'
                    };
                    
                    // Status-Farben
                    const statusColors = {
                      'new': 'primary.main',
                      'feasibility': 'warning.main',
                      'negotiation': 'warning.main',
                      'in_progress': 'warning.main',
                      'completed': 'success.main',
                      'payment_pending': 'info.main',
                      'delivered': 'success.main',
                      'rejected': 'error.main'
                    };
                    
                    const duration = getDuration(dates.startDate, dates.endDate);
                    const isCurrentStatus = status === request.status;
                    
                    return (
                      <Box key={status} sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1,
                        borderLeft: '3px solid',
                        borderColor: statusColors[status] || 'grey.500',
                        bgcolor: isCurrentStatus ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}>
                        <Box>
                          <Typography variant="body2" sx={{ 
                            fontWeight: isCurrentStatus ? 'bold' : 'medium',
                            color: isCurrentStatus ? statusColors[status] : 'text.primary'
                          }}>
                            {statusNames[status] || status}
                            {isCurrentStatus && (
                              <Chip 
                                size="small" 
                                label="Aktuell" 
                                color="primary" 
                                variant="outlined"
                                sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(dates.startDate)}
                            {dates.endDate && ` - ${formatDate(dates.endDate)}`}
                          </Typography>
                        </Box>
                        {duration && (
                          <Chip 
                            size="small" 
                            label={duration} 
                            color="default" 
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 4 }}>
          Anfragen
        </Typography>

        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="request tabs">
              <Tab label="Alle Anfragen" />
              <Tab label={`Neue Anfragen (${requests.filter(r => r.status === 'new').length})`} />
              <Tab label="In Bearbeitung" />
              <Tab label="Abgeschlossen" />
            </Tabs>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <TextField
              placeholder="Suchen..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: '40%' }}
            />
            <Box>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 200, mr: 2 }}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status"
                >
                  <MenuItem value="all">Alle Status</MenuItem>
                  <MenuItem value="new">Neu</MenuItem>
                  <MenuItem value="feasibility">Machbarkeitsprüfung</MenuItem>
                  <MenuItem value="negotiation">In Verhandlung</MenuItem>
                  <MenuItem value="in_progress">In Bearbeitung</MenuItem>
                  <MenuItem value="completed">Abgeschlossen</MenuItem>
                  <MenuItem value="payment_pending">Zahlung ausstehend</MenuItem>
                  <MenuItem value="delivered">Übermittelt</MenuItem>
                  <MenuItem value="rejected">Abgelehnt</MenuItem>
                </Select>
              </FormControl>
              <IconButton onClick={handleFilterClick}>
                <FilterIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleFilterClose}
              >
                <MenuItem onClick={() => { handleFilterClose(); }}>Neueste zuerst</MenuItem>
                <MenuItem onClick={() => { handleFilterClose(); }}>Älteste zuerst</MenuItem>
                <MenuItem onClick={() => { handleFilterClose(); }}>Patientenzahl (hoch-niedrig)</MenuItem>
                <MenuItem onClick={() => { handleFilterClose(); }}>Patientenzahl (niedrig-hoch)</MenuItem>
                <MenuItem onClick={() => { handleFilterClose(); }}>Preis (hoch-niedrig)</MenuItem>
                <MenuItem onClick={() => { handleFilterClose(); }}>Preis (niedrig-hoch)</MenuItem>
              </Menu>
            </Box>
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="Anfragen Tabelle">
              <TableHead>
                <TableRow>
                  <TableCell>Titel</TableCell>
                  <TableCell>Unternehmen</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Patientenzahl</TableCell>
                  <TableCell>Preis</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((request) => {
                    const statusInfo = getStatusInfo(request.status);
                    const isExpanded = expandedRow === request.id;
                    
                    return (
                      <React.Fragment key={request.id}>
                        <TableRow hover>
                          <TableCell component="th" scope="row">
                            {request.title}
                          </TableCell>
                          <TableCell>{request.company}</TableCell>
                          <TableCell>{DateUtils.formatDate(request.date)}</TableCell>
                          <TableCell>
                            <Chip
                              icon={statusInfo.icon}
                              label={statusInfo.label}
                              color={statusInfo.color}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{request.patientCount || '—'}</TableCell>
                          <TableCell>{request.price ? `${request.price} €` : '—'}</TableCell>
                          <TableCell align="right">
                            <Button
                              variant={isExpanded ? "contained" : "outlined"}
                              size="small"
                              onClick={() => {
                                // Immer zuerst die Zeile ein-/ausklappen
                                handleActionClick(request);
                              }}
                              startIcon={<VisibilityIcon />}
                            >
                              {isExpanded ? "Schließen" : getActionButtonText(request.status)}
                            </Button>
                          </TableCell>
                        </TableRow>
                        
                        {/* Ausgeklappter Bereich */}
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                              <Box sx={{ 
                                p: 3, 
                                backgroundColor: 'rgba(0, 0, 0, 0.02)', 
                                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                borderTop: '1px dashed rgba(224, 224, 224, 1)'
                              }}>
                                {renderExpandedContent(request)}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                {filteredRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Keine Anfragen gefunden
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredRequests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
          />
        </Paper>
      </Box>

      {/* Dialog für Anfragendetails */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedRequest && (
          <>
            <DialogTitle>
              {selectedRequest.title}
              <Chip
                icon={getStatusInfo(selectedRequest.status).icon}
                label={getStatusInfo(selectedRequest.status).label}
                color={getStatusInfo(selectedRequest.status).color}
                size="small"
                sx={{ ml: 2 }}
              />
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Unternehmen:</strong> {selectedRequest.company}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Eingangsdatum:</strong> {DateUtils.formatDate(selectedRequest.date)}
                </Typography>
                {selectedRequest.patientCount && (
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Patientenzahl:</strong> {selectedRequest.patientCount}
                  </Typography>
                )}
                {selectedRequest.price && (
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Preis:</strong> {selectedRequest.price} €
                  </Typography>
                )}
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Beschreibung:</strong>
                </Typography>
                <Typography paragraph>
                  {selectedRequest.description}
                </Typography>
              </DialogContentText>
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Nächste Schritte
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={() => {
                      handleCloseDetailDialog();
                      handleActionClick(selectedRequest);
                    }}
                  >
                    {getActionButtonText(selectedRequest.status)}
                  </Button>
                  {selectedRequest.status === 'new' && (
                    <Button 
                      variant="outlined" 
                      color="error"
                      onClick={handleCloseDetailDialog}
                    >
                      Anfrage ablehnen
                    </Button>
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetailDialog}>Schließen</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </MainLayout>
  );
};

export default RequestsPage;
